import { useApolloClient, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { useGroupManagersList } from 'user/hooks/group/useGroupManagersList';
import {
    IContentTypeValue,
    IUserSelectorRole,
    IGroupFragment,
    IGroupUsersQueryVariables,
    AddManagersToGroupDocument,
    RemoveManagersFromGroupDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useUserTableData } from 'user/hooks/useUserTableData';

import { UserSelector } from '../UserSelector';

interface IProps {
    group: IGroupFragment;
    isActive: boolean;
}

export const ManagersView = ({ group, isActive }: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const client = useApolloClient();

    const { id: groupId, permissions } = group;

    const {
        loading: loadingManagers,
        users: managers,
        tableDataProps,
    } = useUserTableData<IGroupUsersQueryVariables>(
        useGroupManagersList,
        // Skip the GroupManagers query until the managers tab is opened so the
        // drawer doesn't fetch managers for a tab the user may never view.
        !isActive,
        { id: groupId }
    );
    const [addManagersToGroup, { loading: loadingAddManagers }] = useMutation(
        AddManagersToGroupDocument
    );
    const [removeManagersFromGroup, { loading: loadingRemoveUsers }] =
        useMutation(RemoveManagersFromGroupDocument);

    const addCountToGroupInCache = (count: number) => {
        // Keep the manager count shown in the groups list in sync.
        client.cache.modify({
            id: `Group:${groupId}`,
            fields: {
                managerCount(existing = 0) {
                    return existing + count;
                },
            },
        });
    };

    const refreshManagerCaches = () => {
        // Evict the selector list (a root-query field) so already-added users
        // show up correctly when the drawer is reopened.
        client.cache.evict({ fieldName: 'paginatedUsers' });
        client.cache.gc();

        client.refetchQueries({ include: ['GroupManagers'] });
    };

    const handleAddManagersToGroup = async (selectedUsers: string[]) => {
        try {
            const result = await addManagersToGroup({
                variables: {
                    groupId,
                    userIds: selectedUsers,
                },
            });

            const { succeededIds, failedIds } =
                result.data?.addManagersToGroup?.response || {};

            if (succeededIds?.length) {
                displaySnackbar(
                    translate('groupForm.managersAddedSuccess', {
                        count: succeededIds.length,
                    }),
                    { variant: 'success' }
                );

                addCountToGroupInCache(succeededIds.length);
                refreshManagerCaches();
            }

            if (failedIds?.length) {
                displaySnackbar(
                    translate('groupForm.managersAddedFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );
            }
        } catch (error) {
            showApolloError(error);
        }
    };

    const handleRemoveManagersFromGroup = async (selectedUsers: string[]) => {
        try {
            const result = await removeManagersFromGroup({
                variables: {
                    groupId,
                    userIds: selectedUsers,
                },
            });

            const { succeededIds, failedIds } =
                result.data?.removeManagersFromGroup?.response || {};

            if (succeededIds?.length) {
                displaySnackbar(
                    translate('groupForm.managersRemovedSuccess', {
                        count: succeededIds.length,
                    }),
                    { variant: 'success' }
                );

                addCountToGroupInCache(-succeededIds.length);
                refreshManagerCaches();
            }

            if (failedIds?.length) {
                displaySnackbar(
                    translate('groupForm.managersRemovedFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );
            }
        } catch (error) {
            showApolloError(error);
        }
    };

    const loading = loadingManagers || loadingAddManagers;

    const permissionsObj = JSON.parse(permissions || '{}');
    const canAddManagers = !!permissionsObj?.canAddManagers;

    return (
        <UserSelector
            addButtonTranslationKeys={{
                noSelection: 'groupForm.addGroupManagers',
                selection: 'groupForm.addGroupManagersWithCount',
            }}
            addedUsers={managers}
            deleteDialogProps={{
                title: translate('groupForm.managerDeleteDialog.title'),
                text: translate('groupForm.managerDeleteDialog.text'),
            }}
            isEditable={canAddManagers}
            label={translate('groupForm.addGroupManagers')}
            loading={loading}
            loadingRemove={loadingRemoveUsers}
            noResultsLabel={translate('noOptionsText.managers')}
            paginationTranslationKey="groupForm.groupManager"
            selectorQueryVariables={{
                contentType: IContentTypeValue.Group,
                objectId: groupId,
                objectRoles: [IUserSelectorRole.GroupManager],
            }}
            tableDataProps={tableDataProps}
            onAddUsers={handleAddManagersToGroup}
            onRemoveUsers={handleRemoveManagersFromGroup}
        />
    );
};
